/**
 * Every word the improvement project pages say (docs/plans/2026-10-05_improvement-projects.md): the statuses and the
 * DMAIC phases, a composed number in plain words, and the lines that tell a person what each section is. Plain and
 * honest: a hypothesis is an association the plant's data bears out or not, never a cause. Pure.
 */
import type {
  ImprovementProject,
  ProjectBaseline,
  ProjectControlRow,
  ProjectMeasurementGap,
  ProjectMethod,
  ProjectPhase,
  ProjectProvenance,
  ProjectQuantity,
  ProjectStatus,
  ProjectTarget,
  ProjectX,
} from "@/lib/api/improvement-projects";
import type { BriefViewCadence } from "@/lib/api/brief";
import { ApiClientError } from "@/lib/api-client";
import { errorCodeOf, errorDetailOf } from "@/lib/api/error";
import { CADENCE_NOUN, dayRangeText, windowLabel } from "@/lib/brief/cadence-words";
import { count, pct, SHARE_UNIT } from "@/lib/brief/words";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";

export const PROJECTS_TITLE = "Improvement projects";
export const PROJECTS_ROUTE = "/app/twin/improvement-projects";

/** A project's page. */
export function projectHref(id: string): string {
  return `${PROJECTS_ROUTE}/${encodeURIComponent(id)}`;
}

export const STATUS_LABEL: Record<ProjectStatus, string> = {
  draft: "Draft",
  active: "Active",
  closed: "Closed",
  dropped: "Dropped",
};

/** A DMAIC's phases, in order. */
export const PHASES: readonly ProjectPhase[] = ["define", "measure", "analyze", "improve", "control"];

/** An A3's phases, in order: plan, do, check, act. */
export const A3_PHASES: readonly ProjectPhase[] = ["plan", "do", "check", "act"];

/** A method's phases, in order. */
export function phasesOf(method: ProjectMethod | undefined): readonly ProjectPhase[] {
  return method === "a3" ? A3_PHASES : PHASES;
}

/** A method's name, short and in a line. */
export const METHOD_LABEL: Record<ProjectMethod, string> = { dmaic: "DMAIC", a3: "A3" };

/** What each method is for, in a line, as the choice offers them. */
export const METHOD_LEAD: Record<ProjectMethod, string> = {
  dmaic: "Six Sigma's define, measure, analyze, improve and control, for a problem that runs high month after month or has several likely causes. About a quarter's work.",
  a3: "One page, plan, do, check and act, for a bounded problem: one shift's or one week's rise, one line, one likely cause, or a change to follow through. A few weeks' work.",
};

export const PHASE_LABEL: Record<ProjectPhase, string> = {
  define: "Define",
  measure: "Measure",
  analyze: "Analyze",
  improve: "Improve",
  control: "Control",
  plan: "Plan",
  do: "Do",
  check: "Check",
  act: "Act",
};

/** What each phase is for, in a line. */
export const PHASE_LEAD: Record<ProjectPhase, string> = {
  define: "The problem, its scope and the goal.",
  measure: "The output the project moves, where it stands now, and the inputs that bear on it.",
  analyze: "What the plant's data says about each mechanism a foundry would look at.",
  improve: "The changes to try, each one tracked as the plant tries it.",
  control: "How the plant holds the gain once it has it.",
  plan: "The problem on one page: its background, where it stands, the goal and its root cause.",
  do: "The countermeasures, step by step, each with who and by when.",
  check: "What the effect check reads of each change the plant tried.",
  act: "Keep what worked, make it the plant's standard, and say what is left.",
};

export const SECTION_LEAD = {
  gaps: "What the plant does not record yet. Until it does, the mechanisms that need these cannot be tested. This project keeps the analysis as it was composed: once the plant records them, a project created afresh from the brief tests them.",
  composed: "Composed from the plant's briefs when the project was created. These numbers are read only.",
  improve: "Actions from the playbook for the mechanisms that bear on this output. Marking one as tried lets the plant check its effect.",
  control: "The plant's limits for the inputs it measures, and what to do when one goes outside them.",
} as const;

/** The status chip's colour, in the app's badge variants. */
export const STATUS_BADGE: Record<ProjectStatus, "secondary" | "info" | "success" | "outline"> = {
  draft: "secondary",
  active: "info",
  closed: "success",
  dropped: "outline",
};

/** A number with its unit, as the brief would say it. A fraction or a rate reads as a percentage. */
export function quantityText(q: ProjectQuantity): string {
  const unit = (q.unit ?? "").trim();
  switch (unit) {
    case "fraction":
    case "rate":
    case "ratio":
      return pct(q.value);
    case "%":
    case "percent":
      return `${round(q.value)}%`;
    case "pieces":
      return `${count(q.value)} pieces`;
    case "":
      return round(q.value);
    default:
      return `${round(q.value)} ${unit}`;
  }
}

function round(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  const abs = Math.abs(v);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return Number(v.toFixed(digits)).toLocaleString("en-US");
}

/**
 * A number as the server sent it: the digits it has and no others. The fewest figures that read back as the same number
 * are kept (0.035 stays 0.035, 1385.5 stays 1,385.5, a whole number gains none), so a plant's own limit is never rounded;
 * only float noise beyond twelve figures (0.30000000000000004) is taken off.
 */
function exactText(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  if (v === 0) return "0";
  let own = 17;
  for (let p = 1; p <= 17; p++) {
    if (Number(v.toPrecision(p)) === v) {
      own = p;
      break;
    }
  }
  return Number(v.toPrecision(Math.min(own, 12))).toLocaleString("en-US", { maximumFractionDigits: 20 });
}

/** A plant day or an instant, as a day: the first ten characters of either. */
function dayOf(s: string): string {
  return s.slice(0, 10);
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** A period's name for a baseline: "August 2026", "Week 34, 17 to 23 Aug 2026", "18 Aug 2026"; a latest closed month says so. */
function periodWords(b: ProjectBaseline): string {
  const period = b.period;
  if (!period) return "";
  const first = dayOf(b.window?.from ?? "");
  const last = dayOf(b.window?.to ?? "");
  const month = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period.period_key);
  let name: string;
  if (period.cadence === "month" && month) name = `${MONTH_NAMES[Number(month[2]) - 1]} ${month[1]}`;
  else if (Object.prototype.hasOwnProperty.call(CADENCE_NOUN, period.cadence)) {
    name = windowLabel({ cadence: period.cadence as BriefViewCadence, period_key: period.period_key, first_day: first, last_day: last });
  } else name = period.period_key;
  // The server's basis says which month it took ("the month 2026-08, the latest closed month"), or for a past project
  // the last month closed before it opened.
  if (/latest closed month/i.test(b.basis ?? "")) return `${name}, the latest closed month`;
  return /closed before the project opened/i.test(b.basis ?? "") ? `${name}, the last month closed before it opened` : name;
}

/**
 * A baseline summed over several period briefs (the three latest closed months): the periods and the days they span
 * ("the 3 latest closed months, May to July 2026"). Its units_n is the periods, never lots, so it is not worded from it.
 */
function periodsWords(b: ProjectBaseline, past: boolean): string {
  const ps = b.periods ?? [];
  if (ps.length === 0) return "";
  const cadence = ps[0].cadence;
  const [one, many] = PERIOD_NOUNS[cadence] ?? ["period", "periods"];
  // A past project's are the last closed before it opened, not the latest now.
  const head = past
    ? ps.length === 1
      ? `the last ${one} closed before it opened`
      : `the ${count(ps.length)} ${many} closed before it opened`
    : ps.length === 1
      ? `the latest closed ${one}`
      : `the ${count(ps.length)} latest closed ${many}`;
  const months = ps.map((p) => /^(\d{4})-(0[1-9]|1[0-2])$/.exec(p.period_key));
  let span = "";
  if (cadence === "month" && months.every((m): m is RegExpExecArray => m !== null)) {
    // Oldest first: the keys are year-month, so they sort as text.
    const keys = ps.map((p) => p.period_key).sort();
    const [fy, fm] = keys[0].split("-");
    const [ly, lm] = keys[keys.length - 1].split("-");
    const last = `${MONTH_NAMES[Number(lm) - 1]} ${ly}`;
    if (keys.length === 1) span = last;
    else span = `${fy === ly ? MONTH_NAMES[Number(fm) - 1] : `${MONTH_NAMES[Number(fm) - 1]} ${fy}`} to ${last}`;
  } else {
    const first = dayOf(b.window?.from ?? "");
    const last = dayOf(b.window?.to ?? "");
    span = first && last ? dayRangeText(first, last) : "";
  }
  return span ? `${head}, ${span}` : head;
}

/** The overall brief's lots window: the lots it rests on and its days ("over 840 lots, 5 Jan to 28 Jun 2026"). */
function lotsWords(b: ProjectBaseline): string {
  const first = dayOf(b.window?.from ?? "");
  const last = dayOf(b.window?.to ?? "");
  const lots = b.units_n > 0 ? `over ${count(b.units_n)} ${b.units_n === 1 ? "lot" : "lots"}` : "";
  const days = first && last ? dayRangeText(first, last) : "";
  return [lots, days].filter(Boolean).join(", ") || (b.basis ?? "");
}

/**
 * A baseline in words: a rate with both its counts and the window it was measured over ("3.1% of castings, 2,418 of
 * 78,000, over 840 lots, 5 Jan to 28 Jun 2026"; a period's "6.2% of castings, 38,000 of 612,000, August 2026"). A
 * period's baseline rests on one period's brief, so units_n is 1 there and is never worded: the period (its cadence and
 * key) and the basis name the window instead. Without the counts (a server that omits them) the rate stands alone.
 */
export function baselineText(p: Pick<ImprovementProject, "ctq"> & { provenance?: ProjectProvenance }): string {
  const b: ProjectBaseline = p.ctq.baseline;
  const past = Boolean(p.provenance?.backfill);
  const where = b.period ? periodWords(b) : b.periods && b.periods.length > 0 ? periodsWords(b, past) : lotsWords(b);
  const counted = typeof b.pieces === "number" && typeof b.castings === "number" && b.castings > 0;
  const head = counted ? `${quantityText(b)} of castings, ${count(b.pieces!)} of ${count(b.castings!)}` : quantityText(b);
  return where ? `${head}, ${where}` : head;
}

/**
 * Why the baseline is over its window, in the server's words: its basis past the window's name ("the month 2026-07
 * scrapped 1 piece for blow hole, fewer than the 5 a rate rests on"), each shorter window it passed over. "" where the
 * basis only names the window (the shortest rested on enough), which baselineText already says.
 */
export function baselineWhyText(b: ProjectBaseline): string {
  const basis = b.basis ?? "";
  const at = basis.indexOf(": ");
  return at < 0 ? "" : basis.slice(at + 2).trim();
}

/** Under a thin baseline (the lots window resting on too little): no change can be judged against it. */
export const THIN_BASELINE_TEXT = "Too few castings or pieces to judge a change against: the target holds it.";

const PERIOD_NOUNS: Record<string, [string, string]> = {
  shift: ["shift", "shifts"],
  day: ["day", "days"],
  week: ["week", "weeks"],
  month: ["month", "months"],
  year: ["year", "years"],
  span: ["day", "days"],
};

/**
 * The period brief's own baseline beside a period's rate: "4.1% over the 4 months before it, 1 Apr to 31 Jul 2026".
 * Nothing for a baseline that is not a period's, or whose brief states none.
 */
export function periodBaselineText(b: ProjectBaseline): string {
  const ref = b.period_baseline;
  const cadence = b.period?.cadence ?? b.periods?.[0]?.cadence;
  if (!ref || !cadence) return "";
  const [one, many] = PERIOD_NOUNS[cadence] ?? ["period", "periods"];
  const n = ref.periods;
  const periods = n === 1 ? `the ${one}` : n > 0 ? `the ${count(n)} ${many}` : `the ${many}`;
  const first = dayOf(ref.window?.from ?? "");
  const last = dayOf(ref.window?.to ?? "");
  return `${pct(ref.value)} over ${periods} before it${first && last ? `, ${dayRangeText(first, last)}` : ""}`;
}

/** "2.0% (half the baseline)". */
export function targetText(t: ProjectTarget): string {
  return t.basis ? `${quantityText(t)} (${t.basis})` : quantityText(t);
}

/** A lower and upper limit in words: "1,380 to 1,420 °C", "at least 1,380 °C", "at most 6%", or nothing. */
export function limitsText(
  lo: number | null | undefined,
  hi: number | null | undefined,
  unit = "",
  prefs?: DisplayPrefs,
): string {
  // A tracking share's ends are a share of the day (brief/v19): written as a percentage of it.
  if (unit === SHARE_UNIT) {
    const pctOf = (v: number | null | undefined) =>
      typeof v === "number" && Number.isFinite(v) ? Number((v * 100).toPrecision(12)) : v;
    return limitsText(pctOf(lo), pctOf(hi), "%");
  }
  // The ends and the unit move into the org's units together (units slice 2b); the plant's figures as sent otherwise.
  const s = shownFor(unit, prefs);
  const shownUnit = s.unit;
  const move = (v: number | null | undefined) =>
    s.converted && typeof v === "number" && Number.isFinite(v) ? Number(s.convert(v).toPrecision(6)) : v;
  lo = move(lo);
  hi = move(hi);
  // A percentage sits against its number ("44%"); any other unit after a space ("1,420 °C").
  const u = shownUnit === "%" ? "%" : shownUnit ? ` ${shownUnit}` : "";
  const has = (v: number | null | undefined): v is number => typeof v === "number" && Number.isFinite(v);
  // A limit is the plant's own figure: it is printed as sent, never rounded (0.035 to 0.045% is not "0.04 to 0.04%").
  if (has(lo) && has(hi)) return `${exactText(lo)} to ${exactText(hi)}${u}`;
  if (has(lo)) return `at least ${exactText(lo)}${u}`;
  if (has(hi)) return `at most ${exactText(hi)}${u}`;
  return "";
}

/** An input's state in Measure: measured or not, its limits, and whether its gauge is verified. */
export function xMeasuredText(x: ProjectX): string {
  return x.measured ? "Measured" : "Not recorded";
}

/** The status card's line over the gaps: what the plant does not record yet (no promise that the project will pick it up). */
export function gapsBannerText(gaps: number): string {
  return `${count(gaps)} ${gaps === 1 ? "measurement" : "measurements"} the plant does not record yet`;
}

/** What the Measure table's limits column says it holds: the plant's window, or the brief's suggested range (never a limit). */
export const X_LIMITS_HEADING = "Window or suggested range";

/**
 * Under an empty control plan: what it means, and what this person can actually do (a project is composed once). Only
 * an open project can be dropped, and only by its creator or an org admin (`canEnd`); a closed or dropped one is done.
 */
export function controlEmptyText(status: ImprovementProject["status"], canEnd: boolean): string {
  const lead =
    "No input in scope has a window on the Limits page, so this plan has no row. A project is composed once, so a window set later does not appear here.";
  if (status !== "draft" && status !== "active") {
    return `${lead} Set the windows on the Limits page before the next project on this output.`;
  }
  return canEnd
    ? `${lead} Set the windows on the Limits page, then drop this project and create it again from the brief.`
    : `${lead} Set the windows on the Limits page; then its creator or an org admin can drop this project, and it can be created again from the brief.`;
}
export const GAPS_NONE_TEXT = "The plant already records every input the mechanisms that bear on this output need.";

/** A hypothesis's evidence line: the server's effect text, with the count added only where the text holds none (it usually ends with it). */
export function evidenceText(e: { n: number; effect_text: string }): string {
  const effect = e.effect_text ?? "";
  if (!(e.n > 0)) return effect;
  // The count is said already where a number of the text is the count (410, or 1,240 for 1240).
  const numbers = effect.match(/\d[\d,]*(?:\.\d+)?/g) ?? [];
  const said = numbers.some((t) => t.replace(/,/g, "") === String(e.n));
  return said ? effect : `${effect} (n = ${count(e.n)})`;
}

/**
 * A scope value with its word ("Line 1"), unless the value already holds the word as a word anywhere, in any case:
 * "Line 1" is Line 1, not Line Line 1, and "Day shift" is not Shift Day shift, but "Lineage 3" is Line Lineage 3. The
 * server names a scope the same way (projectNamed).
 */
export function scopeBadgeText(word: "Line" | "Shift" | "Part" | "Grade", value: string): string {
  const v = value.trim();
  return new RegExp(`(^|[^A-Za-z])${word}([^A-Za-z]|$)`, "i").test(v) ? v : `${word} ${v}`;
}

/**
 * Whether a statement already opens with the mechanism's title, as the server writes it ("Title. Sentence."), apart from
 * case and spacing. A whole-scrap project's statement opens with its defect first ("Blow hole: Title. Sentence."), so
 * the title may follow that lead.
 */
export function opensWithTitle(statement: string, title: string | undefined): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
  const t = norm(title ?? "");
  if (t === "") return false;
  const s = norm(statement);
  if (s.startsWith(t)) return true;
  const lead = /^[^.:]{1,80}: /.exec(s);
  return lead !== null && s.slice(lead[0].length).startsWith(t);
}

export function gaugeText(x: ProjectX): string {
  if (!x.measured) return "";
  if (x.gauge_verified === true) return "Gauge verified";
  if (x.gauge_verified === false) return "Gauge not verified";
  return "Not known";
}

/** Where an X's limits come from, in words. */
export const LIMITS_SOURCE: Record<string, string> = {
  process_window: "the plant's window",
  suggested_range: "the brief's suggested range",
};

export function xLimitsText(x: ProjectX, prefs?: DisplayPrefs): string {
  if (!x.limits) return "No limits set";
  const l = limitsText(x.limits.lo, x.limits.hi, x.unit, prefs);
  if (!l) return "No limits set";
  const from = LIMITS_SOURCE[x.limits.source];
  return from ? `${l} (${from})` : l;
}

/** Whether any X carries a gauge check: the column shows only then (the composer does not carry it yet). */
export function gaugeKnown(xs: readonly ProjectX[]): boolean {
  return xs.some((x) => typeof x.gauge_verified === "boolean");
}

/** A control line's limits with the input's unit (the plan's rows carry none; the Measure table's input of the same name does). */
export function controlLimitsText(r: ProjectControlRow, unit = "", prefs?: DisplayPrefs): string {
  return limitsText(r.lo, r.hi, unit, prefs) || "No limits set";
}

/** A hypothesis's status in words: its own word, spaced and capitalised ("not_tested" reads "Not tested"). */
export function hypothesisStatusText(status: string): string {
  const s = (status ?? "").replace(/_/g, " ").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "";
}

/** The verdicts the brief gives a mechanism, which the page shows as the brief's own badge. */
export function isBriefVerdict(v: string | undefined): v is "supported" | "suggestive" | "not_supported" | "untestable" {
  return v === "supported" || v === "suggestive" || v === "not_supported" || v === "untestable";
}

/** The gaps in the order the plant should close them: a numbered priority first (1 before 2), then the most pieces. */
export function gapsInOrder(gaps: readonly ProjectMeasurementGap[]): ProjectMeasurementGap[] {
  const rank = (g: ProjectMeasurementGap): number => {
    if (typeof g.priority === "number") return g.priority;
    switch (String(g.priority).toLowerCase()) {
      case "high":
        return 1;
      case "medium":
        return 2;
      case "low":
        return 3;
      default:
        return 4;
    }
  };
  return [...gaps].sort((a, b) => rank(a) - rank(b) || b.pieces_at_stake - a.pieces_at_stake);
}

/** A gap's priority as a chip's words: "Priority 1", "High priority", or nothing. */
export function gapPriorityText(g: ProjectMeasurementGap): string {
  if (typeof g.priority === "number") return `Priority ${g.priority}`;
  const p = String(g.priority ?? "").trim();
  return p ? `${p[0].toUpperCase()}${p.slice(1).toLowerCase()} priority` : "";
}

/** "1,200 pieces at stake." */
export function piecesAtStakeText(pieces: number): string {
  return `${count(pieces)} ${pieces === 1 ? "piece" : "pieces"} at stake`;
}

/** The project's scope in a line: its label, else its area. */
export function scopeText(p: Pick<ImprovementProject, "scope">): string {
  return p.scope.label || p.scope.area_label || "The whole plant";
}

/** The timeline: "12 weeks". */
export function weeksText(n: number): string {
  return `${count(n)} ${n === 1 ? "week" : "weeks"}`;
}

/** A created or updated instant as a day: "3 Oct 2026". */
export function dayText(instant: string): string {
  const d = dayOf(instant);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? dayRangeText(d, d) : instant;
}

/** What creating a project said, where it did not open one. */
export const CREATE_OFF_TEXT = "Improvement projects are off for this plant.";
export const CREATE_FAILED_TEXT = "The project could not be created. Try again.";
export const CREATE_NO_BASELINE_TEXT = "The briefs state no rate for this yet, so no project could be composed.";

/** A refused permission, in plain words: never the raw permission code the server names it by. */
export const NOT_ALLOWED_TEXT = "Your role in this plant does not allow that.";
export const NOT_YOURS_TEXT = "Only the person who started this project, or an org admin, can close or drop it.";

/**
 * A 403 insufficient_permission in plain words: the creator-or-admin rule for closing or dropping (the server's detail names
 * the project's creator), else the role's. Undefined for any other failure, which keeps the server's words.
 */
export function forbiddenText(err: unknown): string | undefined {
  if (!(err instanceof ApiClientError) || err.status !== 403 || errorCodeOf(err) !== "insufficient_permission") return undefined;
  return /creator/i.test(errorDetailOf(err) ?? "") ? NOT_YOURS_TEXT : NOT_ALLOWED_TEXT;
}

/** Why a save failed: a refused permission in plain words, else the server's words where it gives them (a closed project is read only). */
export function saveFailureText(err: unknown): string {
  return forbiddenText(err) ?? saveRefusedText(errorDetailOf(err));
}

/** A refusal the server explains (a brief that holds no baseline for it yet), in its words. */
export function createRefusedText(detail: string | undefined): string {
  return detail ? `The project could not be composed: ${detail}` : CREATE_FAILED_TEXT;
}

/**
 * Why a create failed, in the server's words where it gives them: no baseline for the output (422 no_baseline), a typed
 * scope the plant does not know (400 invalid_source, which lists the known ones), a refused permission, a missing brief.
 * A refusal with no words of its own that is no_baseline still says so; any other failure is the plain one.
 */
export function createFailureText(err: unknown): string {
  const refused = forbiddenText(err);
  if (refused) return refused;
  const detail = errorDetailOf(err);
  if (detail) return createRefusedText(detail);
  return errorCodeOf(err) === "no_baseline" ? CREATE_NO_BASELINE_TEXT : CREATE_FAILED_TEXT;
}

/**
 * The page of a project a create opened because one already covered it: whether that one is a draft or active, and the
 * server's advice for making room: drop it, which its creator or an org admin may do. `canEnd` says whether this person
 * is one; left out, the advice is too (the page cannot tell).
 */
export function existingBannerText(status?: string, canEnd?: boolean): string {
  const a = status === "draft" ? "A draft project" : status === "active" ? "An active project" : "A project";
  const open = `${a} on this output and scope is already open, so it opened here instead of a new one.`;
  if (canEnd === undefined) return open;
  return canEnd
    ? `${open} To compose a new one instead, drop this one first.`
    : `${open} To compose a new one instead, its creator or an org admin has to drop this one first.`;
}

/** What a project was composed from, in a line: the brief and its engine, the analysis run behind it, its statistics. */
export function provenanceText(p: ProjectProvenance | undefined): string {
  if (!p) return "";
  const composed = p.brief_generated_at || p.brief_engine
    ? `Composed${p.brief_generated_at ? ` ${dayText(p.brief_generated_at)}` : ""}${p.brief_engine ? ` by ${p.brief_engine}` : ""}`
    : "";
  const inside = [p.stats_version && `statistics ${p.stats_version}`, p.run_id && `run ${p.run_id.slice(0, 8)}`].filter(Boolean).join(", ");
  const run =
    p.run_done_at || inside
      ? `the analysis run${p.run_done_at ? ` that finished ${dayText(p.run_done_at)}` : ""}${inside ? ` (${inside})` : ""}`
      : "";
  const line = composed && run ? `${composed}, from ${run}.` : composed ? `${composed}.` : run ? `From ${run}.` : "";
  return p.backfill ? [PAST_PROJECT_TEXT, line].filter(Boolean).join(" ") : line;
}

/** What a past project's numbers rest on: its baseline is from before it opened, its analysis from when it was recorded. */
export const PAST_PROJECT_TEXT =
  "Recorded after it ran: its baseline is from before it opened, and its analysis is the plant's data when it was recorded.";

/**
 * When a project ran, under its header: a past project's own days and when it was recorded here, else when it started
 * and was last changed.
 */
export function projectDatesText(p: Pick<ImprovementProject, "created_at" | "updated_at" | "provenance">): string {
  const past = p.provenance?.backfill;
  if (!past) return `Started ${dayText(p.created_at)}. Last changed ${dayText(p.updated_at)}.`;
  const ran = past.closed_on ? `Ran ${dayRangeText(past.opened_on, past.closed_on)}` : `Started ${dayText(past.opened_on)}`;
  return `${ran}. Recorded here ${dayText(past.recorded_on || past.recorded_at)}.`;
}

/** The mark beside a timeline or a control frequency the composer defaulted, and what it means. */
export const DEFAULT_WORD = "default";
export const DEFAULT_TITLE = "The composer's default, until a person sets it.";

/** Under the playbook's line in Define: it is textbook knowledge, never the data's finding. */
export const PLAYBOOK_NOTE = "Textbook knowledge, not a finding of this plant's data.";

/**
 * The playbook's line under its label ("The playbook says"): the server's words without their own lead ("The foundry
 * playbook says: "), which would say it twice.
 */
export function playbookSaysText(s: string): string {
  return s.replace(/^\s*the foundry playbook says:\s*/i, "");
}

/** Over the charter's comparison: how the result is judged (the sentence says against what). */
export const COMPARISON_LABEL = "How the result is judged";

/** By the lifecycle's buttons when the person's id could not be read: closing or dropping is the creator's or an admin's. */
export const CALLER_UNREAD_TEXT = "Close and Drop are hidden: who you are could not be read.";

/** The edit form's lines under the goal and the timeline. */
export const GOAL_HINT_COMPOSED = "A new target writes this sentence again, unless you write the goal yourself.";
export const GOAL_HINT_OWN = "You wrote this goal, so a new target leaves it as it is.";
export const TIMELINE_HINT_DEFAULT = "The composer's default. A different number is yours.";
/** The checkbox that makes the composer's default timeline the person's own. */
export function timelineConfirmText(weeks: number): string {
  return `Confirm ${weeksText(weeks)} as the timeline`;
}
/** The line under the target's basis: what it is now, and that a new target needs the person's own words. */
export function targetBasisHint(current: string): string {
  const now = current.trim() ? `Now: ${current.trim()}. ` : "";
  return `${now}Write what a new target is set on in your own words. It is required when you change the target.`;
}
export const TARGET_BASIS_LABEL = "What the new target is set on";

/** Why an edit was not saved, in the server's words where it gives them (a closed project is read only). */
export function saveRefusedText(detail: string | undefined): string {
  return detail ? `The change was not saved: ${detail}` : "The change could not be saved. Try again.";
}

/** The switch, as the admin's control says it. */
export const SWITCH_TITLE = "Improvement projects";
/** What the switch is, and who turns it on or off: never "Off until ..." beside "On for this plant." (2026-10-24, 16.4). */
export const SWITCH_LEAD =
  "A Six Sigma project composed from a trend in the briefs: the problem with its numbers, what to measure, what the data can and cannot test yet, what to try and what to control. An org admin turns it on or off.";

export function switchStateText(enabled: boolean): string {
  return enabled ? "On for this plant." : "Off for this plant.";
}

/**
 * What a page shows while the feature is off: a plain "Not found", and no line about a switch, an org admin or the
 * platform. A plant the feature is not open to is told nothing of it (the page cannot know which of the two gates is shut,
 * nor whether a switch exists for the person to turn).
 */
export const NOT_FOUND_TITLE = "Not found";
export const BACK_TO_APP_TEXT = "Back to the app";
/** Under a list that holds fewer projects than the org has: "Showing 50 of 120 projects". */
export function showingText(shown: number, total: number): string {
  return `Showing ${count(shown)} of ${count(total)} ${total === 1 ? "project" : "projects"}`;
}
/** By "Show more" when its last try failed: the projects shown stay, and the button tries again. */
export const MORE_FAILED_TEXT = "More projects could not be loaded. Show more tries again.";
export const PROJECTS_EMPTY_TITLE = "No improvement project yet";
export const PROJECTS_EMPTY_TEXT =
  "Start one from the Plant brief: each defect, each piece of evidence and each trend in a period brief offers Create project.";
